"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { nav, site, mailto } from "@/lib/site";

type NavChild = { label: string; href: string };
type NavItem = { label: string; href: string; children?: readonly NavChild[] };

export default function Header() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const pathname = usePathname();

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <header className="sticky top-0 z-50 border-b border-line/70 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="container-x flex h-20 items-center justify-between gap-4">
        <Link href="/" className="flex items-center gap-3" aria-label={site.name}>
          <Image
            src="/assets/logo1.webp"
            alt={site.name}
            width={52}
            height={52}
            className="h-11 w-auto"
            priority
          />
          <span className="block text-[0.95rem] font-semibold leading-tight text-ink">
            Golden International
            <span className="block text-xs font-medium text-muted">Contracting Co.</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {(nav as readonly NavItem[]).map((item) => {
            const active = isActive(item.href);

            if (item.children) {
              const groupActive =
                active || item.children.some((c) => isActive(c.href));
              return (
                <div key={item.href} className="group relative">
                  <Link
                    href={item.href}
                    className={`inline-flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                      groupActive ? "text-gold-dark" : "text-ink/80 hover:text-gold-dark"
                    }`}
                    aria-haspopup="true"
                  >
                    {item.label}
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="mt-0.5 transition-transform group-hover:rotate-180"
                    >
                      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Link>
                  {/* Dropdown — appears on hover/focus of the group */}
                  <div className="invisible absolute left-0 top-full z-10 min-w-[13rem] pt-2 opacity-0 transition-all duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                    <div className="overflow-hidden rounded-xl border border-line bg-white py-1.5 shadow-lg shadow-ink/5">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          href={child.href}
                          className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                            isActive(child.href)
                              ? "bg-gold-soft text-gold-dark"
                              : "text-ink/80 hover:bg-surface hover:text-gold-dark"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.href}
                href={item.href}
                className={`rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  active ? "text-gold-dark" : "text-ink/80 hover:text-gold-dark"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <a href={mailto("Inquiry from goldenitc.sa")} target="_blank" rel="noopener noreferrer" className="hidden lg:inline-flex btn btn-gold">
          Get a Quote
        </a>

        {/* Mobile toggle */}
        <button
          type="button"
          className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-ink"
          aria-label="Open main menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {open ? (
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            ) : (
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <nav className="lg:hidden border-t border-line bg-white" aria-label="Mobile">
          <div className="container-x flex flex-col py-3">
            {(nav as readonly NavItem[]).map((item) => {
              const active = isActive(item.href);

              if (item.children) {
                const groupActive =
                  active || item.children.some((c) => isActive(c.href));
                return (
                  <div key={item.href}>
                    <div className="flex items-center">
                      <Link
                        href={item.href}
                        onClick={() => setOpen(false)}
                        className={`flex-1 rounded-lg px-3 py-3 text-[0.98rem] font-medium ${
                          groupActive ? "bg-gold-soft text-gold-dark" : "text-ink/85 hover:bg-surface"
                        }`}
                      >
                        {item.label}
                      </Link>
                      <button
                        type="button"
                        aria-label={`Toggle ${item.label} submenu`}
                        aria-expanded={servicesOpen}
                        onClick={() => setServicesOpen((v) => !v)}
                        className="ml-1 rounded-lg p-2.5 text-ink/70 hover:bg-surface"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                        >
                          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                    {servicesOpen && (
                      <div className="ml-3 flex flex-col border-l border-line pl-3">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            href={child.href}
                            onClick={() => setOpen(false)}
                            className={`rounded-lg px-3 py-2.5 text-[0.95rem] font-medium ${
                              isActive(child.href)
                                ? "bg-gold-soft text-gold-dark"
                                : "text-ink/80 hover:bg-surface"
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className={`rounded-lg px-3 py-3 text-[0.98rem] font-medium ${
                    active ? "bg-gold-soft text-gold-dark" : "text-ink/85 hover:bg-surface"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <a
              href={mailto("Inquiry from goldenitc.sa")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="btn btn-gold mt-3"
            >
              Get a Quote
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
